/**
 * Cache the last known GPS position so callers can fall back when offline or
 * when the geolocation API errors out. Persisted to localStorage — small
 * (~150 bytes) and survives across reloads. iOS WebView eviction can wipe
 * localStorage along with IndexedDB, but a stale-but-recent last position is
 * better than no position when offline.
 *
 * Use `getCurrentPosition()` as a drop-in for `navigator.geolocation.getCurrentPosition`
 * that transparently falls back to the cached value if a fresh fix isn't available.
 */

const STORAGE_KEY = 'cmdbridge.last-location';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface CachedPosition {
  lat: number;
  lng: number;
  accuracy?: number;
  altitude?: number;
  heading?: number;
  speed?: number;
  recorded_at: number;
}

interface GetCurrentPositionOptions {
  timeoutMs?: number;
  maximumAgeMs?: number;
  enableHighAccuracy?: boolean;
  allowStale?: boolean;
}

function read(): CachedPosition | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPosition;
    if (typeof parsed.lat !== 'number' || typeof parsed.lng !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function write(position: CachedPosition): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  } catch {
    // localStorage may be unavailable (private mode, quota); ignore.
  }
}

class LastLocationService {
  recordPosition(position: GeolocationPosition | CachedPosition): void {
    if ('coords' in position) {
      write({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy ?? undefined,
        altitude: position.coords.altitude ?? undefined,
        heading: position.coords.heading ?? undefined,
        speed: position.coords.speed ?? undefined,
        recorded_at: position.timestamp || Date.now(),
      });
    } else {
      write({ ...position, recorded_at: position.recorded_at || Date.now() });
    }
  }

  getLastKnown(maxAgeMs: number = MAX_AGE_MS): CachedPosition | null {
    const position = read();
    if (!position) return null;
    if (Date.now() - position.recorded_at > maxAgeMs) return null;
    return position;
  }

  /**
   * Drop-in replacement for navigator.geolocation.getCurrentPosition. Tries
   * a fresh fix first; on timeout or error returns the cached position if
   * recent enough. Never throws — returns null when nothing is usable.
   */
  async getCurrentPosition(
    options: GetCurrentPositionOptions = {}
  ): Promise<CachedPosition | null> {
    const {
      timeoutMs = 10_000,
      maximumAgeMs = 5_000,
      enableHighAccuracy = true,
      allowStale = true,
    } = options;

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return allowStale ? this.getLastKnown() : null;
    }

    return new Promise<CachedPosition | null>((resolve) => {
      let settled = false;
      const settle = (value: CachedPosition | null) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const cached: CachedPosition = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy ?? undefined,
            altitude: pos.coords.altitude ?? undefined,
            heading: pos.coords.heading ?? undefined,
            speed: pos.coords.speed ?? undefined,
            recorded_at: pos.timestamp || Date.now(),
          };
          write(cached);
          settle(cached);
        },
        () => {
          settle(allowStale ? this.getLastKnown() : null);
        },
        { enableHighAccuracy, timeout: timeoutMs, maximumAge: maximumAgeMs }
      );
    });
  }

  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

export const lastLocationService = new LastLocationService();
